<script setup lang="ts">
  import { useRoute } from 'vue-router'
  import BaseDocument from './BaseDocument.vue'
  import { computed } from 'vue'
  import PDFPageLoop from '@/components/page/PDFPageLoop.vue'
  import CertificatePageLoop from '@/components/page/CertificatePageLoop.vue'
  import { useBlockElements } from '@/composable/useBlockElements'
  import { useFillableFieldsV2 } from '@/composable/useFillableFieldsV2'
  import { useDocumentStore } from '@/store/document'
  import PDFPageSection from '@/components/page/PDFPageSection.vue'
  import { IElement, PageElementOptions } from '@gohighlevel/ghl-proposals-common'

  const route = useRoute()
  const isOnlyCertificate = computed(() => route?.query?.onlyCertificate == 'true')
  const { isAllBlockElementsLoaded } = useBlockElements()
  const { isAllFillableFieldsLoaded } = useFillableFieldsV2()
  const store = useDocumentStore()
  const { flatBlockElements, flatFillableFields } = store
  const isPageRendererComplete = computed(() => {
    if (isOnlyCertificate.value) return true
    const areBlocksReady = flatBlockElements.length === 0 || isAllBlockElementsLoaded.value

    const areFieldsReady = flatFillableFields.length === 0 || isAllFillableFieldsLoaded.value

    return areBlocksReady && areFieldsReady
  })

  const pagination = computed(() => {
    if (route?.query?.from && route?.query?.to) {
      return { from: route?.query?.from, to: route?.query?.to }
    }
    return {}
  })

  // Get first page for header/footer snapshot
  const firstPage = computed(() => {
    if (!store.document?.pages || store.document.pages.length === 0) {
      return null
    }
    return store.document.pages[0] as IElement<PageElementOptions>
  })

  // Get header elements from first page
  const headerElements = computed(() => {
    return firstPage.value?.component?.options?.headers?.children || []
  })

  // Get footer elements from first page
  const footerElements = computed(() => {
    return firstPage.value?.component?.options?.footers?.children || []
  })

  // Check if header/footer exist
  const hasHeader = computed(() => {
    return headerElements.value.length > 0
  })

  const hasFooter = computed(() => {
    return footerElements.value.length > 0
  })

  // Page margins for aligning header/footer content with page content (same left/right as content area)
  const pageMargins = computed(() => {
    const dims = firstPage.value?.component?.options?.pageDimensions
    if (!dims?.margins) return { left: 48, right: 48 }
    return dims.margins
  })

  // Page numbering options for webshot: read from query (from e.g. proposal-builder GroupHeaders toggle) or first page
  const enablePagination = computed(() => {
    const fromQuery = route?.query?.enablePagination
    if (fromQuery !== undefined && fromQuery !== null) {
      return fromQuery === 'true'
    }
    return firstPage.value?.component?.options?.enablePageNumbering ?? true
  })
  const pageNumberFormat = computed(() => {
    const format = firstPage.value?.component?.options?.pageNumberFormat
    return format === 'x-of-y' ? 'x-of-y' : 'number-only'
  })
  const pageNumberLocation = computed(() => {
    const location = firstPage.value?.component?.options?.pageNumberLocation
    return location === 'header' ? 'header' : 'footer'
  })
  const enablePaginationValue = computed(() => (enablePagination.value ? 'true' : 'false'))

  // Header/footer section styles (background, etc.) from first page – match builder
  const headerSectionStyles = computed(() => {
    const section = firstPage.value?.component?.options?.headers
    return {
      paddingLeft: pageMargins.value.left + 'px',
      paddingRight: pageMargins.value.right + 'px',
      backgroundColor: section?.responsiveStyles?.large?.backgroundColor || 'white',
      backgroundImage: section?.component?.options?.src ? `url('${section.component.options.src}')` : undefined,
      backgroundPosition: section?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: section?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: '100%',
      opacity: section?.responsiveStyles?.large?.opacity != null ? section.responsiveStyles.large.opacity / 100 : 1,
    }
  })

  const footerSectionStyles = computed(() => {
    const section = firstPage.value?.component?.options?.footers
    return {
      paddingLeft: pageMargins.value.left + 'px',
      paddingRight: pageMargins.value.right + 'px',
      backgroundColor: section?.responsiveStyles?.large?.backgroundColor || 'white',
      backgroundImage: section?.component?.options?.src ? `url('${section.component.options.src}')` : undefined,
      backgroundPosition: section?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: section?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: '100%',
      opacity: section?.responsiveStyles?.large?.opacity != null ? section.responsiveStyles.large.opacity / 100 : 1,
    }
  })
</script>

<template>
  <BaseDocument v-slot="props">
    <template v-if="!props.loading">
      <!-- Header snapshot for PDF generation -->
      <div
        id="pdf-header-snapshot"
        v-if="!isOnlyCertificate && hasHeader && firstPage"
        class="pdf-snapshot-header"
        :style="headerSectionStyles"
      >
        <PDFPageSection :page="firstPage" :elements="headerElements" :is-in-header-or-footer="true" />
      </div>
      <!-- Footer snapshot for PDF generation -->
      <div
        id="pdf-footer-snapshot"
        v-if="!isOnlyCertificate && hasFooter && firstPage"
        class="pdf-snapshot-footer"
        :style="footerSectionStyles"
      >
        <PDFPageSection :page="firstPage" :elements="footerElements" :is-in-header-or-footer="true" />
      </div>
      <div id="page-renderer-complete" v-if="isPageRendererComplete" />
      <div id="page-renderer" v-if="!isOnlyCertificate">
        <!-- Pagination options for webshot: read via #page-renderer [id^="page-header-pagination-*"]; enablePagination from query (e.g. GroupHeaders toggle) or document -->
        <div
          id="page-header-pagination-enable"
          aria-hidden="true"
          style="position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden"
        >
          {{ enablePaginationValue }}
        </div>
        <div
          id="page-header-pagination-format"
          aria-hidden="true"
          style="position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden"
        >
          {{ pageNumberFormat }}
        </div>
        <div
          id="page-header-pagination-location"
          aria-hidden="true"
          style="position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden"
        >
          {{ pageNumberLocation }}
        </div>
        <PDFPageLoop :is-preview="false" :pagination="pagination" />
      </div>
      <div id="certificate-renderer" v-if="isOnlyCertificate && props.hasCertificate">
        <CertificatePageLoop />
      </div>
    </template>
  </BaseDocument>
</template>

<style scoped lang="scss">
  /* Match builder: .page-header-content uses box-sizing: border-box so content area = width - margins.
   * Viewer must do the same: 816px total width including padding → content area = 720px for fillable positioning. */
  .pdf-snapshot-header,
  .pdf-snapshot-footer {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    width: 816px;
    box-sizing: border-box;
    background: white;
    z-index: 99999;
    pointer-events: none;
    visibility: visible;
  }

  .pdf-snapshot-header {
    top: 0;
  }

  .pdf-snapshot-footer {
    bottom: 0;
  }
</style>
